/**
 * PadelTempo hero: a 3D racket (120 pre-rendered Blender frames drawn on a canvas) that is turned by dragging it with the
 * mouse or a finger (like the turntable of the racket finder, assets/pt-racket-spinner.js), lime dust and magnetic buttons.
 * Neither the page scroll nor the mouse position moves the scene or the racket. After a flick the racket keeps gliding,
 * the arrow keys turn it for keyboard users, and one turn plays when the page opens.
 * No dependencies. The first frame is already in the markup as a plain image, so the hero works without this script.
 */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
// how quickly a flicked racket slows down (milliseconds for ~63% of the speed to fade), the same as the finder turntable
const EASE_MS = 650;
// speed (main frames per millisecond) under which a glide stops and the racket settles on a frame instead of crawling on
const TAIL = 0.001;

/** Slow lime dust drifting over the scene. Additive blend is set in CSS (mix-blend-mode: screen). */
class Dust {
  constructor(canvas, accent) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.accent = accent;
    this.items = [];
    this.visible = true;
    this.resize();
    const count = window.innerWidth < 750 ? 40 : 90;
    for (let i = 0; i < count; i++) this.items.push(this.spawn(true));
  }

  spawn(anywhere) {
    return {
      x: Math.random(),
      y: anywhere ? Math.random() : 1.05,
      r: 0.6 + Math.random() * 2.2,
      vx: (Math.random() - 0.35) * 0.012,
      vy: -(0.01 + Math.random() * 0.03),
      a: 0.15 + Math.random() * 0.6,
      tw: Math.random() * Math.PI * 2,
    };
  }

  resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const r = this.canvas.getBoundingClientRect();
    this.w = this.canvas.width = Math.max(1, Math.round(r.width * dpr));
    this.h = this.canvas.height = Math.max(1, Math.round(r.height * dpr));
    this.dpr = dpr;
  }

  draw(dt) {
    const { ctx, w, h } = this;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = this.accent;
    ctx.globalCompositeOperation = 'lighter'; // additive inside the canvas: no CSS blend mode over the whole hero
    for (const it of this.items) {
      it.x += it.vx * dt;
      it.y += it.vy * dt;
      it.tw += dt * 1.5;
      if (it.y < -0.05 || it.x < -0.05 || it.x > 1.05) Object.assign(it, this.spawn(false));
      ctx.globalAlpha = it.a * (0.6 + 0.4 * Math.sin(it.tw));
      ctx.beginPath();
      ctx.arc(it.x * w, it.y * h, it.r * this.dpr, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }
}

class PtHero {
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector('.pt-hero__stage') || root;
    this.canvas = root.querySelector('[data-pt-canvas]');
    this.mirror = root.querySelector('[data-pt-mirror]');
    this.mctx = this.mirror?.getContext('2d');
    try {
      this.urls = JSON.parse(root.dataset.frames || '[]');
    } catch {
      this.urls = [];
    }
    // Phones get every 4th frame, resized by the CDN (Shopify hands Safari a PNG of every .webp asset, about 10x the size,
    // so 120 full-size frames would be tens of megabytes); the finder spinner asks for the same files, so they are shared.
    // With data saver on, the poster image stays and no frames are fetched.
    const phone = window.matchMedia('(max-width: 749px), (pointer: coarse)').matches;
    if (phone) {
      this.urls = navigator.connection?.saveData ? [] : this.urls.filter((_, i) => i % 4 === 0).map((url) => `${url}${url.includes('?') ? '&' : '?'}width=320`);
    }
    this.phone = phone;
    this.interactive = root.classList.contains('pt-hero--spin');
    // A mouse on a desktop with enough memory also gets 120 in-between frames (1.5 degrees after each main frame,
    // assets/pt-racket-mid-NNN.webp) and the position is cross-faded between the two nearest frames, like the turntable of
    // the racket finder: slow turns and the end of a glide then move continuously instead of stepping from frame to frame.
    this.dense = !phone && this.urls.length > 0 && window.matchMedia('(pointer: fine)').matches && !(navigator.deviceMemory && navigator.deviceMemory < 8);
    this.scale = this.dense ? 2 : 1; // positions per main frame
    this.total = this.urls.length * this.scale;
    this.frames = new Array(this.total); // indexed by position: even = main frame, odd = in-between frame
    this.current = 0; // float frame index being drawn
    this.shown = -1; // frame index on the canvas
    this.dirty = true;
    this.velocity = 0; // frames per millisecond, after a flick
    this.dragging = false;
    this.lastX = 0;
    this.lastMove = 0;
    this.raf = 0;
    this.last = performance.now();
    this.ctx = this.canvas?.getContext('2d');
    if (!this.ctx || !this.urls.length) return;

    this.resize = this.resize.bind(this);
    this.tick = this.tick.bind(this);

    const accent = getComputedStyle(root).getPropertyValue('--pt-hero-accent').trim() || '#d7ff3f';
    const dustCanvas = root.querySelector('[data-pt-dust]');
    this.dust = dustCanvas && !reduceMotion.matches && !phone ? new Dust(dustCanvas, accent) : null;

    this.load().then(() => this.start());
    this.bindMagnetic();
  }

  async load() {
    // first frame first so the swap from the poster image is instant
    this.bitmapWidth = this.pickWidth();
    this.frames[0] = await this.fetchFrame(this.urls[0]);
    this.resize();
    this.draw(0);
    this.canvas.classList.add('is-ready');
    // wait until the page itself is done, then fetch the rest coarse-to-fine (every 8th, 4th, 2nd, remaining)
    // so the spin works early with fewer frames and sharpens as the rest arrive
    if (document.readyState !== 'complete') await new Promise((r) => window.addEventListener('load', r, { once: true }));
    const n = this.urls.length;
    const seen = new Set([0]);
    const get = (position, url) => () =>
      this.fetchFrame(url).then((im) => {
        this.frames[position] = im;
        this.dirty = true; // a closer frame may have arrived for the one on screen
      });
    // a few frames at a time with a short pause in between: fetching and decoding them all at once froze a phone for a quarter of
    // a second at a time while the page was still settling
    const pool = async (jobs, limit) => {
      let next = 0;
      const worker = async () => {
        while (next < jobs.length) {
          await jobs[next++]();
          await new Promise((r) => setTimeout(r, 20));
        }
      };
      await Promise.all(Array.from({ length: limit }, worker));
    };
    const run = async (step) => {
      const batch = [];
      for (let i = 0; i < n; i += step) {
        if (!seen.has(i)) {
          seen.add(i);
          batch.push(get(i * this.scale, this.urls[i]));
        }
      }
      await pool(batch, this.phone ? 2 : 4);
    };
    await run(this.phone ? 8 : 4); // enough for a coarse spin: start interacting now (the intro turn of a desktop waits for a finer set)
    (async () => {
      for (const step of [4, 2, 1]) await run(step);
      // the in-between frames come last; a position without one shows the nearest frame that has arrived
      if (this.dense) await pool(this.urls.map((url, i) => get(i * 2 + 1, url.replace('pt-racket-', 'pt-racket-mid-'))), 4);
    })();
  }

  /** Width every frame is decoded at: what the canvas shows, never more than the source and at most about 220 MB (300 MB with the in-between frames) of decoded frames. */
  pickWidth() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const shown = (this.canvas.getBoundingClientRect().width || 640) * dpr;
    const cap = Math.sqrt(((this.dense ? 300e6 : 220e6) * 942) / (this.total * 4 * 687));
    return Math.round(Math.min(942, shown, cap));
  }

  fetchFrame(url) {
    return new Promise((resolve) => {
      const im = new Image();
      im.decoding = 'async';
      im.fetchPriority = 'low'; // the frames must never delay the page itself
      im.onload = async () => {
        // every frame is decoded once, at the size it is drawn, into an ImageBitmap: nothing is decoded or uploaded while the racket turns
        try {
          const w = Math.min(this.bitmapWidth, im.naturalWidth);
          resolve(await createImageBitmap(im, { resizeWidth: w, resizeHeight: Math.round((w * im.naturalHeight) / im.naturalWidth), resizeQuality: this.phone ? 'medium' : 'high' }));
        } catch {
          resolve(im);
        }
      };
      im.onerror = () => resolve(null);
      im.src = url;
    });
  }

  start() {
    this.started = true;
    window.addEventListener('resize', this.resize, { passive: true });
    if (!this.interactive) return; // turning switched off in the theme editor: keep the static first frame
    const { canvas, root } = this;
    // the whole hero turns the racket, not just the picture of it: on a phone the heading and the buttons cover most of the
    // racket, and a swipe that starts on them has to count too (links and buttons keep their own taps)
    root.addEventListener('pointerdown', (e) => this.down(e));
    root.addEventListener('pointermove', (e) => this.move(e));
    root.addEventListener('pointerup', (e) => this.up(e));
    root.addEventListener('pointercancel', (e) => this.up(e));
    canvas.addEventListener('keydown', (e) => this.key(e));
    canvas.addEventListener('dragstart', (e) => e.preventDefault());
    this.io = new IntersectionObserver(([e]) => {
      this.onscreen = e.isIntersecting;
      if (this.onscreen) this.wake();
    });
    this.io.observe(this.stage);
    // intro: spin one turn and land on the first frame (not on a phone: the turn runs while the frames are still being decoded and
    // stutters, and the racket stands still until it is touched anyway)
    if (reduceMotion.matches || this.phone) return;
    this.current = this.urls.length;
    this.introStart = performance.now();
    this.wake();
  }

  wake() {
    if (!this.raf) {
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }
  }

  down(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target instanceof Element && e.target.closest('a, button, input, select, textarea, [role="button"]')) return;
    this.dragging = true;
    this.introStart = 0;
    this.velocity = 0;
    this.lastX = e.clientX;
    this.lastMove = performance.now();
    this.root.setPointerCapture(e.pointerId);
    this.canvas.classList.add('is-dragging');
    this.wake();
  }

  move(e) {
    if (!this.dragging) return;
    const now = performance.now();
    // one full turn takes about 1.3 times the width of the racket
    const delta = (-(e.clientX - this.lastX) * this.urls.length) / ((this.canvas.clientWidth || 480) * 1.3);
    this.current += delta;
    // smoothed speed of the finger, used when it lets go
    this.velocity = this.velocity * 0.6 + (delta / Math.max(1, now - this.lastMove)) * 0.4;
    this.lastX = e.clientX;
    this.lastMove = now;
    this.wake();
  }

  up(e) {
    if (!this.dragging) return;
    this.dragging = false;
    this.canvas.classList.remove('is-dragging');
    if (this.root.hasPointerCapture(e.pointerId)) this.root.releasePointerCapture(e.pointerId);
    // a finger that rested before lifting does not throw the racket
    if (performance.now() - this.lastMove > 90) this.velocity = 0;
    this.wake();
  }

  key(e) {
    const unit = (4 * this.urls.length) / 120;
    const step = e.key === 'ArrowRight' ? unit : e.key === 'ArrowLeft' ? -unit : 0;
    if (!step) return;
    e.preventDefault();
    this.introStart = 0;
    this.velocity = 0;
    this.current += step;
    this.wake();
  }

  tick(now) {
    const n = this.urls.length;
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    if (this.introStart) {
      const t = Math.min(1, (now - this.introStart) / 1800);
      const e = 1 - Math.pow(1 - t, 3);
      this.current = n * (1 - e);
      if (t === 1) this.introStart = 0;
    } else if (!this.dragging) {
      this.settling = false;
      if (this.velocity !== 0) {
        // glide: the speed fades out smoothly after a flick
        this.velocity *= Math.exp((-dt * 1000) / EASE_MS);
        this.current += this.velocity * dt * 1000;
        // the last crawl is cut short: the racket then settles on a frame instead of creeping on, one step at a time, for seconds
        if (Math.abs(this.velocity) < TAIL * (this.dense ? 1 : 6)) this.velocity = 0;
      } else if (this.dense) {
        // settle on the nearest position, so the racket never rests in the middle of a cross-fade
        const step = 1 / this.scale;
        const diff = Math.round(this.current / step) * step - this.current;
        this.settling = Math.abs(diff) > 0.002;
        this.current += this.settling ? diff * (1 - Math.exp(-dt / 0.09)) : diff;
      }
    }
    this.draw(this.current);
    if (this.dust) {
      // the dust drifts slowly: 30 steps a second are plenty and leave the main thread to the turning racket
      this.dustDt = (this.dustDt || 0) + dt;
      if (this.dustDt >= 1 / 30) {
        this.dust.draw(this.dustDt);
        this.dustDt = 0;
      }
    }
    const idle = !this.dust && !this.introStart && !this.dragging && this.velocity === 0 && !this.settling;
    // keep the dust alive while the hero is on screen, otherwise sleep
    this.raf = idle || this.onscreen === false ? 0 : requestAnimationFrame(this.tick);
  }

  resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const r = this.canvas.getBoundingClientRect();
    if (!r.width) return;
    this.canvas.width = Math.round(r.width * dpr);
    this.canvas.height = Math.round(r.height * dpr);
    this.ctx.imageSmoothingQuality = 'high';
    if (this.mirror) {
      this.mirror.width = Math.round(r.width * dpr * 0.5);
      this.mirror.height = Math.round(r.height * dpr * 0.5);
    }
    this.dust?.resize();
    this.dirty = true;
    this.draw(this.current);
  }

  /** The frame at a position, or the nearest one that has arrived yet. */
  frameAt(position) {
    const n = this.total;
    for (let d = 0; d < n; d++) {
      const a = this.frames[(position + d) % n] || this.frames[(position - d + n) % n];
      if (a) return a;
    }
    return null;
  }

  draw(f) {
    const n = this.total;
    const { ctx, canvas } = this;
    // with the in-between frames the position is cross-faded between the two nearest frames (in tenths); without them
    // (a phone, little memory) the nearest frame is drawn, because blending two frames 3 degrees apart doubles the edges
    const p = (((f * this.scale) % n) + n) % n;
    let base = Math.floor(p);
    let fraction = 0;
    if (this.dense) fraction = Math.round((p - base) * 10) / 10;
    else base = Math.round(p) % n;
    if (fraction === 1) {
      base = (base + 1) % n;
      fraction = 0;
    }
    const key = base + ':' + fraction;
    if (key === this.shown && !this.dirty) return; // the picture did not change
    const first = this.frameAt(base);
    this.shown = key;
    this.dirty = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.globalAlpha = 1;
    if (first) ctx.drawImage(first, 0, 0, canvas.width, canvas.height);
    if (fraction > 0) {
      const second = this.frameAt((base + 1) % n);
      if (second && second !== first) {
        ctx.globalAlpha = fraction;
        ctx.drawImage(second, 0, 0, canvas.width, canvas.height);
        ctx.globalAlpha = 1;
      }
    }
    // the reflection only needs a small copy, drawn from the frame itself (not read back from the canvas)
    if (this.mctx) {
      const m = this.mirror;
      this.mctx.clearRect(0, 0, m.width, m.height);
      if (first) this.mctx.drawImage(first, 0, 0, m.width, m.height);
    }
  }

  bindMagnetic() {
    if (reduceMotion.matches || !window.matchMedia('(hover: hover)').matches) return;
    this.root.querySelectorAll('[data-pt-magnetic]').forEach((el) => {
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) / r.width;
        const y = (e.clientY - r.top - r.height / 2) / r.height;
        el.style.transform = `translate(${x * 10}px, ${y * 8}px)`;
      });
      el.addEventListener('pointerleave', () => (el.style.transform = ''));
    });
  }
}

document.querySelectorAll('[data-pt-hero]').forEach((el) => new PtHero(el));
